import { useQuery } from '@tanstack/react-query';
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI';
import SpecificAppUsersReportTable from './Table/SpecificAppUsersReport';
import 'jspdf-autotable';
import axios from 'axios';
import { useSelector } from 'react-redux';
import toasters from '../../../../../../../utils/toasters/Toasters';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useSearchParams } from "react-router-dom";


const formatPayloadDates = (payload) => {
  if (!payload) return payload;

  const formattedPayload = { ...payload };

  if (formattedPayload.startDate) {
    const startDate = new Date(formattedPayload.startDate);
    if (!isNaN(startDate)) {
      formattedPayload.startDate = startDate.toISOString().split('T')[0];
    }
  }

  if (formattedPayload.endDate) {
    const endDate = new Date(formattedPayload.endDate);
    if (!isNaN(endDate)) {
      formattedPayload.endDate = endDate.toISOString().split('T')[0];
    }
  }

  // Remove device_type from payload if it's 0 (All Devices), null, or undefined
  if (formattedPayload.device_type === 0 || formattedPayload.device_type === null || formattedPayload.device_type === undefined) {
    delete formattedPayload.device_type;
  }

  return formattedPayload;
};

const SpecificAppUsersReport = () => {
  const location = useLocation();
  const { token } = useSelector((state) => state?.auth);
  const [searchParams] = useSearchParams();

    // Initialize payload from location.state, sessionStorage, or URL query params
    const [payload, setPayload] = useState(() => {
      if (location.state) return location.state;

      const stored = sessionStorage.getItem("specificAppUserReportPayload");
      if (stored) return JSON.parse(stored);

      const params = Object.fromEntries([...searchParams]);
      return Object.keys(params).length ? params : null;
    });

    const [sort, setSort] = useState(null);
    const [formattedPayload, setFormattedPayload] = useState(() =>
      formatPayloadDates(payload)
    );

    useEffect(() => {
      if (location.state) {
        const prevStored = sessionStorage.getItem("specificAppUserReportPayload");
        const prev = prevStored ? JSON.parse(prevStored) : null;

        if (JSON.stringify(prev) !== JSON.stringify(location.state)) {
          sessionStorage.setItem(
            "specificAppUserReportPayload",
            JSON.stringify(location.state)
          );

          setPayload(location.state);
          setFormattedPayload(formatPayloadDates(location.state));
        }
      }
    }, [location.state]);

    const getSpecificAppUserReport = useCallback(async () => {
      if (!formattedPayload) return [];
      let apiQueryParams = {};

      if (sort?.columnName && sort?.order) {
        apiQueryParams = {
          order: sort.order,
          sort: sort.columnName,
        };
      }

      return fetchIncidentAnalysisData({
        ...formattedPayload,
        ...apiQueryParams,
        export: "N",
      });
    }, [formattedPayload, sort]);

    const { data: SpecificAppUsersReportData, isLoading } = useQuery(
      ["fetch-specific-app-users-report", formattedPayload, sort],
      () => getSpecificAppUserReport(),
      {
        enabled: !!formattedPayload, 
        retry: (failureCount, error) => {
          const status = error?.response?.status;
          return failureCount < 1 && status >= 400 && status < 600;
        },
        retryDelay: 1000,
      }
    );

    
  const handleColumnSort = async (column, sortDirection) => {
    setSort((prev) => ({
      ...prev,
      columnName: column.sortField,
      order: sortDirection,
    }));
    // No need to manually invalidate - React Query will handle the sort dependency change
  };

 const downloadPDF = () => {
  const rows = SpecificAppUsersReportData?.users;

  if (!rows || rows.length === 0) {
    toasters({ type: 'error', message: 'No data available to export' });
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: [297, 210],
  });

  doc.setFontSize(18);
  doc.text('Specific App Users Report', 14, 22);

  doc.setFontSize(10);
  if (formattedPayload?.startDate && formattedPayload?.endDate) {
    doc.text(
      `From ${formattedPayload.startDate} To ${formattedPayload.endDate}`,
      14,
      30
    );
  }

  const tableColumns = [
    'User Name',
    'Email',
    'Devices',
  ];

  const tableRows = rows.map((row) => [
    row?.name_tx || '',
    row?.email_tx || '',
    row?.devices || 0,
  ]);

  doc.autoTable({
    head: [tableColumns],
    body: tableRows,
    startY: 40,
    theme: 'grid',
  });

  doc.save('specific-app-users-report.pdf');
};

  const downloadFile = (data, fileName = 'download') => {
    const csvBlob = new Blob([data], { type: 'text/csv' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const hiddenElement = document.createElement('a');
    hiddenElement.href = csvUrl;
    hiddenElement.target = '_blank';
    hiddenElement.download = `${fileName}.csv`;
    hiddenElement.click();
  };

  const ExcelSpecificAppUserReport = async () => {
    try {
      let response = await axios.post(
        `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
        { ...formattedPayload, export: 'Y' },
        {
          headers: {
            'Content-type': 'application/json',
            Authorization: 'Bearer ' + token,
          },
        }
      );
      downloadFile(response?.data, 'specific-app-user');
    } catch (error) {
      toasters({ type: 'error', message: error?.response?.data?.message });
    }
  };

  return (
    <>
      <SpecificAppUsersReportTable
        DisplayDate={formattedPayload}
        SpecificAppUsersReportData={SpecificAppUsersReportData}
        handleColumnSort={handleColumnSort}
        downloadExcel={ExcelSpecificAppUserReport}
        downloadPDF={downloadPDF}
        isLoading={isLoading}
        reportCode={payload?.code}
      />
    </>
  );
};

export default SpecificAppUsersReport;
